<!-- ----------------------------------------- template ----------------------------------------- -->
<template>
  <div>
    <div class="instruction-box">
      <h3>Club Documents</h3>
      <p>Upload and manage club-related documents. Maximum file size: 10MB. Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, Images, ZIP, RAR.</p>
    </div>

    <div class="documents-header">
      <div class="storage-info">
        <span class="storage-label">Storage Used: </span>
        <span class="storage-value">{{ formatFileSize(storageUsed) }} / 1 GB</span>
        <el-progress
          :percentage="storagePercentage"
          :color="storageColor"
          :show-text="false"
          style="width: 200px; margin-left: 12px;"
        ></el-progress>
      </div>
      <el-button type="primary" size="small" @click="showUploadDialog = true">
        <i class="el-icon-upload"></i> Upload Document
      </el-button>
    </div>

    <empty-state
      v-if="!documentList || documentList.length === 0"
      message="No documents yet"
      description="Upload your first document to share files with club members!"
      style="margin-top: 20px"
    ></empty-state>

    <el-table
      v-else
      :data="documentList"
      style="margin-top: 20px"
      :highlight-current-row="false"
      :highlight-selection-row="false"
      :header-cell-class-name="'custom-head-cell'"
      :row-class-name="'custom-row-bg'"
    >
      <el-table-column label="Name" min-width="250">
        <template slot-scope="scope">
          <div style="display: flex; align-items: center;">
            <i :class="getFileIcon(scope.row.file_extension)" style="margin-right: 8px; font-size: 18px;"></i>
            <span>{{ scope.row.name }}</span>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="Size" width="100">
        <template slot-scope="scope">
          {{ formatFileSize(scope.row.file_size) }}
        </template>
      </el-table-column>

      <el-table-column label="Uploaded By" width="150">
        <template slot-scope="scope">
          {{ scope.row.uploader_name || '-' }}
        </template>
      </el-table-column>

      <el-table-column label="Upload Date" width="140">
        <template slot-scope="scope">
          {{ formatDate(scope.row.create_time) }}
        </template>
      </el-table-column>

      <el-table-column label="Operation" width="200" fixed="right">
        <template slot-scope="scope">
          <div style="display: flex; gap: 8px;">
            <el-button type="text" size="small" @click="downloadDocument(scope.row)" style="padding: 0;">
              <i class="el-icon-download"></i> Download
            </el-button>
            <el-button
              type="danger"
              size="small"
              @click="handleDelete(scope.row)"
            >
              Delete
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- Upload Dialog -->
    <el-dialog
      title="Upload Document"
      :visible.sync="showUploadDialog"
      width="700px"
      :close-on-click-modal="false"
    >
      <el-form :model="uploadForm" :rules="uploadRules" ref="uploadForm" label-width="120px">
        <el-form-item label="File" prop="file" required>
          <el-upload
            ref="upload"
            :action="uploadAction"
            :data="uploadData"
            :headers="uploadHeaders"
            :before-upload="beforeUpload"
            :on-change="handleFileChange"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            :file-list="fileList"
            :limit="1"
            :auto-upload="false"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.zip,.rar,.7z"
          >
            <el-button slot="trigger" size="small" type="primary">Select File</el-button>
            <div slot="tip" class="el-upload__tip">
              Max file size: 10MB. Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, Images, ZIP, RAR
            </div>
          </el-upload>
        </el-form-item>

        <el-form-item label="Document Name" prop="name" required>
          <el-input
            v-model="uploadForm.name"
            placeholder="Enter document name"
          ></el-input>
        </el-form-item>

        <el-form-item label="Description" prop="description">
          <el-input
            type="textarea"
            :rows="3"
            v-model="uploadForm.description"
            placeholder="Enter document description (optional)"
          ></el-input>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="showUploadDialog = false">Cancel</el-button>
        <el-button type="primary" @click="submitUpload" :loading="uploading">
          Upload
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<!-- ------------------------------------------ script ------------------------------------------ -->
<script>
import { postRequest } from '@/api/eclub'
import { getUserId, getUserType } from '@/utils/store'
import EmptyState from '@/components/EmptyState.vue'
import * as qiniu from 'qiniu-js'

export default {
  name: 'Documents',
  components: {
    EmptyState
  },
  props: {
    clubInfo: {
      type: Object,
      default: () => ({}),
    },
    club_id: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      documentList: [],
      showUploadDialog: false,
      uploading: false,
      currentUserId: getUserId(),
      currentUserType: getUserType(),
      uploadForm: {
        name: '',
        description: ''
      },
      uploadRules: {
        name: [
          { required: true, message: 'Please enter document name', trigger: 'blur' }
        ]
      },
      fileList: [],
      uploadAction: '',
      uploadData: {},
      uploadHeaders: {},
      qiniuToken: '',
      selectedFile: null,
      observable: null,
      storageUsed: 0
    }
  },

  computed: {
    storagePercentage() {
      const maxStorage = 1073741824 // 1GB in bytes
      return Math.min(Math.round((this.storageUsed / maxStorage) * 100), 100)
    },
    storageColor() {
      if (this.storagePercentage >= 90) return '#F56C6C'
      if (this.storagePercentage >= 75) return '#E6A23C'
      return '#67C23A'
    }
  },

  created () {
    this.fetchDocuments()
  },

  methods: {
    async fetchDocuments() {
      try {
        const response = await postRequest('getClubDocuments', {
          club_id: this.club_id
        })

        if (response.data.code === 0) {
          this.documentList = response.data.data
          // Calculate total storage used
          this.storageUsed = this.documentList.reduce((total, doc) => {
            return total + (doc.file_size || 0)
          }, 0)
        } else {
          this.$message.error('Failed to fetch documents')
        }
      } catch (error) {
        console.error('Error fetching documents:', error)
        this.$message.error('Failed to fetch documents')
      }
    },

    async getQiniuToken() {
      try {
        const response = await postRequest('getSingaporeQiniuToken', {})
        if (response.data.code === 0) {
          return response.data.data
        }
        return null
      } catch (error) {
        console.error('Error getting Qiniu token:', error)
        return null
      }
    },

    handleFileChange(file, fileList) {
      // This is called when a file is selected or removed
      if (file && file.raw) {
        const rawFile = file.raw

        // Validate file size (10MB)
        const maxSize = 10 * 1024 * 1024 // 10MB
        if (rawFile.size > maxSize) {
          this.$message.error('File size cannot exceed 10MB')
          this.$refs.upload.clearFiles()
          this.selectedFile = null
          return
        }

        // Validate file type
        const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'jpg', 'jpeg', 'png', 'gif', 'zip', 'rar', '7z']
        const fileName = rawFile.name
        const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase()

        if (!allowedExtensions.includes(fileExtension)) {
          this.$message.error('File type not allowed. Please upload a valid document file.')
          this.$refs.upload.clearFiles()
          this.selectedFile = null
          return
        }

        // Block executable files
        const blockedExtensions = ['exe', 'bat', 'cmd', 'com', 'pif', 'scr', 'vbs', 'js', 'jar', 'sh', 'app', 'deb', 'rpm', 'php', 'asp', 'aspx']
        if (blockedExtensions.includes(fileExtension)) {
          this.$message.error('File type not allowed for security reasons')
          this.$refs.upload.clearFiles()
          this.selectedFile = null
          return
        }

        this.selectedFile = rawFile
        // Auto-fill the document name with the filename
        this.uploadForm.name = rawFile.name
      } else {
        this.selectedFile = null
        this.uploadForm.name = ''
      }
    },

    beforeUpload(file) {
      // Prevent auto upload
      return false
    },

    async submitUpload() {
      if (!this.selectedFile) {
        this.$message.warning('Please select a file to upload')
        return
      }

      // Check storage limit (1GB = 1073741824 bytes)
      const maxStorage = 1073741824 // 1GB
      const newTotalStorage = this.storageUsed + this.selectedFile.size

      if (newTotalStorage > maxStorage) {
        const exceeded = newTotalStorage - maxStorage
        this.$message.error(
          `Cannot upload file. Storage limit exceeded by ${this.formatFileSize(exceeded)}. ` +
          `Current usage: ${this.formatFileSize(this.storageUsed)} / 1 GB`
        )
        return
      }

      this.uploading = true

      try {
        // Get Qiniu token
        const token = await this.getQiniuToken()
        if (!token) {
          throw new Error('Failed to get upload token')
        }

        // Generate unique file key
        const timestamp = Date.now()
        const fileKey = `eclub-documents/${timestamp}_${this.selectedFile.name}`

        // Upload to Qiniu using observable pattern like EAE project
        this.observable = qiniu.upload(
          this.selectedFile,
          fileKey,
          token
        )

        const observer = {
          next: (result) => {
            // Progress callback
            console.log('Upload progress:', result.total.percent)
          },
          error: (error) => {
            console.error('Upload error:', error)
            this.$message.error('Failed to upload file to cloud storage')
            this.uploading = false
          },
          complete: async (res) => {
            console.log('Upload complete:', res)

            // Construct file URL - using the same pattern as EAE project
            const fileUrl = `https://ap.learningfirst.io/${res.key}`

            // Save document information to database
            try {
              const response = await postRequest('uploadClubDocument', {
                club_id: this.club_id,
                user_id: this.currentUserId,
                user_type: this.currentUserType,
                name: this.uploadForm.name,
                file_name: this.selectedFile.name,
                file_key: res.key,
                file_url: fileUrl,
                file_size: this.selectedFile.size,
                description: this.uploadForm.description
              })

              if (response.data.code === 0) {
                this.$message.success('Document uploaded successfully')
                this.showUploadDialog = false
                this.resetUploadForm()
                this.fetchDocuments()
              } else {
                this.$message.error(response.data.message || 'Failed to save document information')
              }
            } catch (error) {
              console.error('Error saving document:', error)
              this.$message.error('Failed to save document information')
            }

            this.uploading = false
          }
        }

        this.observable.subscribe(observer)
      } catch (error) {
        console.error('Upload error:', error)
        this.$message.error('Failed to upload document')
        this.uploading = false
      }
    },

    resetUploadForm() {
      this.uploadForm = {
        name: '',
        description: ''
      }
      this.selectedFile = null
      this.fileList = []
      if (this.$refs.upload) {
        this.$refs.upload.clearFiles()
      }
    },

    handleUploadSuccess(response, file, fileList) {
      console.log('Upload success:', response)
    },

    handleUploadError(error, file, fileList) {
      console.error('Upload error:', error)
      this.$message.error('Failed to upload file')
    },

    async handleDelete(document) {
      try {
        await this.$confirm('Are you sure you want to delete this document?', 'Warning', {
          type: 'warning'
        })

        const response = await postRequest('deleteClubDocument', {
          document_id: document._id.$id
        })

        if (response.data.code === 0) {
          this.$message.success('Document deleted successfully')
          this.fetchDocuments()
        } else {
          this.$message.error(response.data.message || 'Failed to delete document')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error deleting document:', error)
          this.$message.error('Failed to delete document')
        }
      }
    },

    downloadDocument(document) {
      if (document.file_url) {
        window.open(document.file_url, '_blank')
      } else {
        this.$message.warning('Download link not available')
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
    },

    formatDate(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    getFileIcon(extension) {
      const iconMap = {
        'pdf': 'el-icon-document',
        'doc': 'el-icon-document',
        'docx': 'el-icon-document',
        'xls': 'el-icon-tickets',
        'xlsx': 'el-icon-tickets',
        'ppt': 'el-icon-data-line',
        'pptx': 'el-icon-data-line',
        'txt': 'el-icon-document',
        'csv': 'el-icon-tickets',
        'jpg': 'el-icon-picture',
        'jpeg': 'el-icon-picture',
        'png': 'el-icon-picture',
        'gif': 'el-icon-picture',
        'zip': 'el-icon-folder',
        'rar': 'el-icon-folder',
        '7z': 'el-icon-folder'
      }
      return iconMap[extension?.toLowerCase()] || 'el-icon-document'
    }
  }
}
</script>

<!-- ------------------------------------------ style ------------------------------------------ -->
<style scoped lang="scss">
.instruction-box {
  background-color: #f5f7fa;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 24px;

  h3 {
    color: #303133;
    margin: 0 0 8px 0;
    font-size: 16px;
  }

  p {
    color: #606266;
    margin: 0;
    font-size: 14px;
    line-height: 1.4;
  }
}

.documents-header {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .storage-info {
    display: flex;
    align-items: center;
    font-size: 14px;

    .storage-label {
      color: #606266;
      font-weight: 500;
    }

    .storage-value {
      color: #303133;
      font-weight: 600;
      margin-left: 4px;
    }
  }
}

.custom-head-cell {
  background: #FF8833;
  color: white;
  box-sizing: border-box;
  font-family: GravityBold;
}

:deep(.el-table) {
  .el-button--text {
    padding: 0;
    margin-right: 16px;
    color: #409EFF;

    &:hover {
      color: #66b1ff;
    }
  }

  .el-button--danger {
    padding: 7px 15px;
    margin-right: 16px;
    background-color: white;
    border: 1px solid #F56C6C;
    color: #F56C6C;

    &:hover {
      background-color: #F56C6C;
      color: white;
    }
  }
}

:deep(.el-upload) {
  width: 100%;

  .el-upload__tip {
    margin-top: 8px;
    color: #909399;
    font-size: 12px;
    line-height: 1.5;
  }
}
</style>
